import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, InstagramLogo, FacebookLogo } from "@phosphor-icons/react";
import { ASSETS, API_BASE, INSTAGRAM_URL, FACEBOOK_URL } from "@/lib/constants";
import StoreBadges from "@/components/StoreBadges";
import { reopenCookieBanner } from "@/components/CookieBanner";

const { wordmark: WORDMARK } = ASSETS;
const API = API_BASE;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Enter your email first.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        role: null,
        source: "footer-newsletter",
      });
      toast.success("You're on the list. See you in your inbox.");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't add. Try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      id="download"
      data-testid="site-footer"
      className="relative bg-black border-t border-white/10 noise"
    >
      {/* Big Download CTA */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32 border-b border-white/10">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-5">
              § 06 · live on the app store
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-extrabold tracking-tighter leading-[0.92]">
              Get it now.
              <br />
              <span className="italic font-light">14 days free.</span>
            </h2>
            <p className="mt-6 text-base text-white/55 max-w-lg">
              Stable-ish is live on iOS with a 14-day free trial. Then
              $9.99/mo or $79.99/yr — Founding Member rate locked in for life.
              Download today, poke around, and let us know what&apos;s missing.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-5" data-testid="footer-cta-block">
            <div
              className="flex items-center gap-3"
              data-testid="footer-free-badge"
            >
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FF2A2A] opacity-70" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#FF2A2A]" />
              </span>
              <span className="font-display text-xl sm:text-2xl font-extrabold tracking-tight">
                14 days free
                <span className="text-[#FF2A2A]"> — </span>
                <span className="italic font-light text-white/80">then $9.99/mo or $79.99/yr</span>
              </span>
            </div>
            <StoreBadges testIdPrefix="footer-store" source="footer" />
            <p
              className="font-display text-sm sm:text-base font-bold tracking-tight text-white/85"
              data-testid="footer-availability"
            >
              Available on <span className="text-white">iOS</span> only for now.
              <span className="text-white/55 font-normal italic ml-1.5">
                Android coming soon.
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Newsletter — "stay in the loop" */}
      <div id="newsletter" className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-20 border-b border-white/10">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/45 mb-4">
              § 07 · stay in the loop
            </div>
            <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[0.98]">
              Get notified when
              <span className="italic font-light text-white/70"> Android drops</span>
              <br />
              and Stable-ish ships new bedside tools.
            </h3>
            <p className="mt-4 text-sm text-white/55 max-w-lg">
              Android launch alerts and product updates. No spam.
              Unsubscribe anytime.
            </p>
          </div>
          <form
            onSubmit={onSubmit}
            className="lg:col-span-5"
            data-testid="footer-newsletter-form"
          >
            <div className="border border-white/15 bg-black focus-within:border-[#FF2A2A] focus-within:red-glow transition-colors flex items-center">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your email"
                data-testid="footer-email-input"
                className="flex-1 bg-transparent px-5 py-5 text-white placeholder-white/35 font-mono text-sm focus:outline-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-submit-button"
                className="bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white px-5 py-5 transition-colors"
                aria-label="Subscribe"
              >
                <ArrowRight size={18} weight="bold" />
              </button>
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
              Android launch · product updates · new bedside tools
            </p>
          </form>
        </div>
      </div>

      {/* Footer meta */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5 flex items-start gap-5">
          <div className="relative overflow-hidden h-14 w-[260px] shrink-0">
            <img
              src={WORDMARK}
              alt="Stable-ish"
              draggable="false"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-w-none pointer-events-none"
              style={{ height: "360px" }}
            />
          </div>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 max-w-[18ch]">
            AI clinical assistant for ER & critical care nurses
          </p>
        </div>

        <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40 mb-3">
              product
            </div>
            <ul className="space-y-1 text-sm text-white/75">
              <li>
                <a href="/#features" className="block py-2 hover:text-[#FF2A2A]">
                  Features
                </a>
              </li>
              <li>
                <a href="/#use-cases" className="block py-2 hover:text-[#FF2A2A]">
                  Use Cases
                </a>
              </li>
              <li>
                <a href="/#faq" className="block py-2 hover:text-[#FF2A2A]">
                  FAQ
                </a>
              </li>
              <li>
                <Link
                  to="/subscribe"
                  data-testid="footer-subscribe-link"
                  className="block py-2 hover:text-[#FF2A2A] transition-colors"
                >
                  Email updates
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40 mb-3">
              legal
            </div>
            <ul className="space-y-1 text-sm text-white/75">
              <li>
                <Link
                  to="/terms"
                  data-testid="footer-terms-link"
                  className="block py-2 hover:text-[#FF2A2A] transition-colors"
                >
                  Terms
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  data-testid="footer-privacy-link"
                  className="block py-2 hover:text-[#FF2A2A] transition-colors"
                >
                  Privacy
                </Link>
              </li>
              <li>
                <Link
                  to="/medical-disclaimer"
                  data-testid="footer-medical-disclaimer-link"
                  className="block py-2 hover:text-[#FF2A2A] transition-colors"
                >
                  Medical Disclaimer
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={reopenCookieBanner}
                  data-testid="footer-cookie-settings"
                  className="block py-2 hover:text-[#FF2A2A] transition-colors text-left"
                >
                  Cookie settings
                </button>
              </li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40 mb-3">
              contact
            </div>
            <ul className="space-y-1 text-sm text-white/75">
              <li>
                <a
                  href="mailto:hello@stableish.me"
                  className="block py-2 hover:text-[#FF2A2A] transition-colors"
                  data-testid="footer-email-link"
                >
                  hello@stableish.me
                </a>
              </li>
              <li>
                <Link
                  to="/feedback"
                  className="block py-2 hover:text-[#FF2A2A] transition-colors"
                  data-testid="footer-feedback-link"
                >
                  Send feedback
                </Link>
              </li>
              <li>
                <Link
                  to="/share"
                  className="block py-2 hover:text-[#FF2A2A] transition-colors"
                  data-testid="footer-share-link"
                >
                  Share with a nurse friend
                </Link>
              </li>
              <li className="flex items-center gap-2 pt-1">
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Stable-ish on Instagram"
                  className="inline-flex items-center justify-center w-11 h-11 md:w-9 md:h-9 border border-white/15 hover:border-[#FF2A2A] hover:text-[#FF2A2A] text-white/70 transition-colors"
                  data-testid="footer-instagram-link"
                >
                  <InstagramLogo size={16} weight="bold" />
                </a>
                <a
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Stable-ish on Facebook"
                  className="inline-flex items-center justify-center w-11 h-11 md:w-9 md:h-9 border border-white/15 hover:border-[#FF2A2A] hover:text-[#FF2A2A] text-white/70 transition-colors"
                  data-testid="footer-facebook-link"
                >
                  <FacebookLogo size={16} weight="bold" />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Fine print — disclaimer + version */}
      <div className="border-t border-white/10">
        <div
          className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 space-y-3"
          data-testid="footer-fine-print"
        >
          <p className="text-xs sm:text-sm text-white/45 leading-relaxed max-w-4xl">
            Stable-ish is an educational and reference tool for licensed
            clinicians. It does not provide medical advice, diagnosis, or
            treatment, and does not replace your hospital&rsquo;s policies, your
            pharmacy, or your provider&rsquo;s orders. Always verify before
            acting.
          </p>
          <p
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40"
            data-testid="footer-version-line"
          >
            iOS only ·{" "}
            <a
              href="mailto:support@stableish.me"
              className="hover:text-[#FF2A2A] transition-colors"
              data-testid="footer-support-email"
            >
              support@stableish.me
            </a>{" "}
            · Current version: v1.1.5
          </p>
        </div>
      </div>

      {/* Disclaimer band */}
      <div className="border-t border-[#FF2A2A]/40 bg-[#FF2A2A]/[0.04]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-6 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <Link
            to="/medical-disclaimer"
            data-testid="footer-disclaimer-band-link"
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF2A2A] hover:text-[#FF4D4D] transition-colors"
          >
            ⚠ for licensed healthcare professionals · not a substitute for clinical judgment · verify with institutional protocol
          </Link>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
            © {new Date().getFullYear()} Stable-ish LLC · <Link to="/terms" className="inline-block py-2 hover:text-white/70">Terms</Link> · <Link to="/privacy" className="inline-block py-2 hover:text-white/70">Privacy</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
